import { useEffect, useRef, useState } from 'react';

export function usePollingController(callback: (signal: AbortSignal) => void | Promise<void>, intervalMs: number = 500) {
  const savedCallback = useRef(callback);
  const timerRef = useRef<number | null>(null);
  const shouldRunRef = useRef(false);
  const requestRef = useRef<AbortController | null>(null);

  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const clear = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    clear();
    if (document.hidden) {
      // dont start if hidden.
      setIsPaused(true);
      return;
    }
    setIsPaused(false);
    timerRef.current = window.setInterval(async () => {
      if (requestRef.current || !shouldRunRef.current) return;
      const request = new AbortController();
      requestRef.current = request;
      try {
        await savedCallback.current(request.signal);
      } catch {
        // Keep the current history and retry on the next tick.
      } finally {
        requestRef.current = null;
      }
    }, intervalMs);
  };

  const start = () => {
    if (shouldRunRef.current) return;
    shouldRunRef.current = true;
    setIsRunning(true);
    startTimer();
  };

  const stop = () => {
    shouldRunRef.current = false;
    requestRef.current?.abort();
    setIsRunning(false);
    setIsPaused(false);
    clear();
  };

  //pause when hidden, resume when visible.
  useEffect(() => {
    const onVis = () => {
      if (!shouldRunRef.current) return;
      if (document.hidden) {
        setIsPaused(true);
        clear();
      } else {
        startTimer();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [intervalMs]);

  useEffect(() => () => {
    shouldRunRef.current = false;
    requestRef.current?.abort();
    clear();
  }, []);

  return { start, stop, isRunning, isPaused } as const;
}

/**
 * Example
 *
 * function randomFunction() {
 *   const { start, stop, isRunning, isPaused } = usePollingController(() => aDifferentRandomFunction(), 500);
 *
 *   return (
 *     <div>
 *       <button onClick={start} disabled={isRunning && !isPaused}>Start</button>
 *       <button onClick={stop} disabled={!isRunning}>Stop</button>
 *       {isRunning && (isPaused ? <span>Paused (tab hidden)</span> : <span>Running</span>)}
 *     </div>
 *   );
 * }
 */
